// ─────────────────────────────────────────────────────────────────────────
// Hearth — API type definitions (mirrors API_CONTRACT.md exactly)
// ─────────────────────────────────────────────────────────────────────────

export type AbilityKey = "str" | "dex" | "con" | "int" | "wis" | "cha";
export const ABILITY_KEYS: AbilityKey[] = ["str", "dex", "con", "int", "wis", "cha"];

export type Scores = Record<AbilityKey, number>;

export type DmStyle = "balanced" | "gritty" | "heroic" | "comedic" | "horror";
export const DM_STYLES: DmStyle[] = ["balanced", "gritty", "heroic", "comedic", "horror"];

export type ControllerKind = "human" | "ai";
export type SeatKind = "human" | "ai" | "empty";

export type LogKind =
  | "narration"
  | "speech"
  | "action"
  | "system"
  | "roll"
  | "ooc"
  | "dm";

// ── Health & SRD meta ──────────────────────────────────────────────────────
export interface HealthResponse {
  ok: boolean;
  llm: boolean;
  stats: Record<string, number>;
}

export interface MetaAbility {
  index: AbilityKey;
  name: string;
}
export interface MetaSkill {
  index: string;
  name: string;
  ability: AbilityKey;
}
export interface MetaNamed {
  index: string;
  name: string;
}
export interface MetaSubrace {
  index: string;
  name: string;
  race: string;
}
export interface MetaSubclass {
  index: string;
  name: string;
}
export interface MetaClass {
  index: string;
  name: string;
  hit_die: number;
  spellcasting: boolean;
  saving_throws: AbilityKey[];
  subclasses: MetaSubclass[];
}
export interface SrdMeta {
  abilities: MetaAbility[];
  skills: MetaSkill[];
  races: MetaNamed[];
  subraces: MetaSubrace[];
  classes: MetaClass[];
  backgrounds: MetaNamed[];
  alignments: MetaNamed[];
  standard_array: number[];
  point_buy_costs: Record<string, number>;
}

export interface SpellBrief {
  index: string;
  name: string;
  school?: string;
}
export interface ClassSpellsResponse {
  class: string;
  by_level: Record<string, SpellBrief[]>;
}

// ── Dice ────────────────────────────────────────────────────────────────────
export type Advantage = "normal" | "advantage" | "disadvantage";

export interface RollGroup {
  notation: string;
  count: number;
  sides: number;
  rolls: number[];
  kept: number[];
  dropped: number[];
  subtotal: number;
}
export interface RollResult {
  expression: string;
  total: number;
  modifier: number;
  detail: string;
  advantage: Advantage;
  crit: boolean;
  natural: number | null;
  groups: RollGroup[];
}
export interface AbilityArrayResponse {
  rolls: RollResult[];
  totals: number[];
}

// ── Character builder ─────────────────────────────────────────────────────
export interface BuildCharacterRequest {
  name: string;
  race_index: string;
  class_index: string;
  background_index?: string | null;
  subrace_index?: string | null;
  subclass_index?: string | null;
  level: number;
  scores?: Scores | null;
  skill_proficiencies: string[];
  skill_expertise?: string[];
  feats?: string[];
  alignment: string;
  cantrips: string[];
  spells: string[];
  controller_kind: ControllerKind;
}

export interface PointBuyResponse {
  valid: boolean;
  message: string;
  cost: number;
}

// ── progression & rest ──
export interface LevelUpOptions {
  class_index: string;
  class_name: string;
  from_level: number;
  to_level: number;
  total_level_after: number;
  hit_die: number;
  average_hp: number;
  con_mod: number;
  features: string[];
  needs_subclass: boolean;
  subclass_options: MetaSubclass[];
  grants_asi: boolean;
  is_caster: boolean;
  new_spell_levels: number[];
  is_new_class: boolean;
}
export interface LevelUpRequest {
  class_index: string;
  subclass_index?: string | null;
  hp_method: "average" | "roll" | "max";
  asi?: Record<string, number> | null;
  new_cantrips: string[];
  new_spells: string[];
}
export interface LevelUpResult {
  summary: {
    name: string;
    level_before: number;
    level_after: number;
    class: string;
    class_level: number;
    subclass: string | null;
    hp_gained: number;
    hp_detail: string;
    max_hp: number;
    asi_applied: Record<string, number>;
    new_scores: Scores;
    features_gained: string[];
    spells_added: string[];
    spell_slots: Record<string, number>;
    proficiency_bonus: number;
  };
  character: FullCharacter;
}
export interface RestRequest {
  character_ids?: string[] | null;
  hit_dice?: Record<string, Record<string, number>> | null;
}
export interface RestResult {
  kind: "short" | "long";
  results: {
    character: string;
    healed?: number;
    current_hp: number;
    max_hp?: number;
    hit_dice_spent?: Record<string, number>;
    hit_dice?: Record<string, number>;
    exhaustion?: number;
  }[];
}

// Generic result for in-play character actions (equip, use, cast, slot).
// Carries action-specific fields plus the refreshed full character.
export interface CharacterActionResult {
  ok: boolean;
  character: FullCharacter;
  name?: string;
  equipped?: boolean;
  ac?: number;
  removed?: string;
  remaining?: number;
  // cast
  spell?: string;
  level?: number;
  cast_at_level?: number | null;
  is_cantrip?: boolean;
  concentration?: boolean;
  save_dc?: number | null;
  attack_bonus?: number | null;
  slots_available?: Record<string, number>;
  // use-item
  effect?: {
    type?: string;
    rolled?: number;
    detail?: string;
    healed?: number;
    current_hp?: number;
  };
  // slot restore
  available?: number;
}

// In-combat options for a PC's turn.
export interface WeaponAttackOption {
  index: string;
  name: string;
  to_hit: number;
  damage: string;
  damage_type: string;
  ranged: boolean;
  finesse: boolean;
  proficient: boolean;
  equipped: boolean;
  two_handed_damage: string | null;
}
export interface CombatTargetOption {
  id: string;
  name: string;
  // Allies (party) carry exact numbers; foes are redacted to a wound tier only —
  // players never learn an enemy's AC or exact HP (matches the role-scoped snapshot).
  hp?: number;
  max_hp?: number;
  ac?: number;
  alive: boolean;
  wound_tier?: string;
  wound_frac?: number;
  conditions?: string[];
}
export interface CombatEconomy {
  used_action: boolean;
  used_bonus: boolean;
  used_reaction: boolean;
  movement_used: number;
  speed: number;
}
export interface CombatSpellOption {
  index: string;
  name: string;
  level: number;
}
// A limited-use / activated class feature (Rage, Action Surge, Second Wind, …).
export interface CombatFeature {
  key: string;
  name: string;
  action: string; // "action" | "bonus" | "reaction" | "special" | "free"
  recharge: string; // "short" | "long" | "at-will" | …
  desc: string;
  remaining: number;
  at_will: boolean;
}
export interface CombatOptions {
  in_combat: boolean;
  is_turn: boolean;
  weapons: WeaponAttackOption[];
  spells: CombatSpellOption[];
  features: CombatFeature[];
  active_effects: string[];
  foes: CombatTargetOption[];
  allies: CombatTargetOption[];
  economy: CombatEconomy | null;
}
// GET …/features
export interface CombatFeaturesResponse {
  features: CombatFeature[];
  active_effects: string[];
}

// ── Full character ──────────────────────────────────────────────────────────
export interface ClassEntry {
  class: string;
  subclass: string | null;
  level: number;
}
export interface SpellDetail {
  desc: string;
  higher_level: string;
  school: string | null;
  casting_time: string | null;
  range: string | null;
  components: string;
  duration: string | null;
  concentration: boolean;
  ritual: boolean;
  mechanic: string;
  homebrew: boolean;
}
export interface SpellEntry {
  index: string;
  name: string;
  level: number;
  prepared: boolean;
  detail?: SpellDetail | null;
}
export interface InventoryItem {
  index: string;
  name: string;
  quantity: number;
  kind: string;
  equipped: boolean;
  attuned?: boolean;
  requires_attunement?: boolean;
  magic_bonus?: string | null;
  meta: Record<string, unknown>;
}
export interface DeathSaves {
  successes: number;
  failures: number;
  stable: boolean;
}
export interface FullCharacter {
  id: string;
  name: string;
  summary: string;
  race: string;
  subrace: string | null;
  background: string | null;
  alignment: string;
  classes: ClassEntry[];
  level: number;
  xp: number;
  proficiency_bonus: number;
  scores: Scores;
  modifiers: Scores;
  saving_throws: Record<string, number>;
  save_proficiencies: string[];
  skills: Record<string, number>;
  skill_proficiencies: string[];
  skill_expertise: string[];
  ac: number;
  max_hp: number;
  current_hp: number;
  temp_hp: number;
  initiative: number;
  speed?: number;
  size?: string;
  score_breakdown?: Record<string, { base: number; racial: number; item: number; total: number }>;
  senses?: { darkvision?: number };
  damage_resistances?: string[];
  currency?: { pp: number; gp: number; ep: number; sp: number; cp: number };
  armor_proficiencies?: string[];
  weapon_proficiencies?: string[];
  languages?: string[];
  race_traits?: { race: string; name: string; real: boolean; desc: string }[];
  passive_perception: number;
  hit_dice: Record<string, number>;
  spell_ability: AbilityKey | null;
  spell_save_dc: number | null;
  spell_attack_bonus: number | null;
  spell_slots: Record<string, number>;
  spell_slots_used: Record<string, number>;
  spells: SpellEntry[];
  inventory: InventoryItem[];
  conditions: string[];
  concentration: string | null;
  exhaustion: number;
  inspiration: boolean;
  feats: { index: string; name: string; desc: string }[];
  subclass_features: { subclass: string; name: string; level: number; real: boolean; desc: string }[];
  death_saves: DeathSaves;
  controller: string | null;
  controller_kind: ControllerKind;
  notes: string;
}

// ── Auth ──────────────────────────────────────────────────────────────────
export interface AuthUser {
  id: string;
  username: string;
  display_name: string;
  is_admin?: boolean;
}

// ── Builder option detail + class progression (explain-everything) ──────────
export interface OptionDetail {
  kind: string;
  index: string;
  name: string;
  desc: string;
  // race
  speed?: number;
  size?: string;
  ability_bonuses?: { ability: string; bonus: number }[];
  languages?: string[];
  traits?: { name: string; desc: string; real?: boolean }[];
  mechanics?: Record<string, unknown>;
  // class
  hit_die?: number;
  saving_throws?: string[];
  proficiencies?: string[];
  spellcasting?: boolean;
  // subclass
  features?: { name: string; level: number; real?: boolean; desc: string }[];
  // ability
  full_name?: string;
  skills?: string[];
  // skill
  ability?: string;
  // background
  feat?: string;
  starting_proficiencies?: string[];
  // spell
  level?: number;
  school?: string;
  casting_time?: string;
  range?: string;
  duration?: string;
  concentration?: boolean;
  ritual?: boolean;
  higher_level?: string;
}

export interface ProgressionLevel {
  level: number;
  prof_bonus: number;
  features: { index: string; name: string; desc: string }[];
  asi: boolean;
  cantrips_known?: number | null;
  spell_slots: Record<string, number>;
  class_specific: Record<string, unknown>;
}
export interface ClassProgression {
  class: string;
  name: string;
  hit_die: number;
  levels: ProgressionLevel[];
}

export interface EditCharacterPatch {
  name?: string;
  alignment?: string;
  scores?: Partial<Record<AbilityKey, number>>;
  skill_proficiencies?: string[];
  skill_expertise?: string[];
  feats?: string[];
  max_hp_override?: number | null;
  current_hp?: number;
  notes?: string;
  gender?: string;
  cantrips?: string[];
  spells?: string[];
  persona?: string;
  label?: string;
}

export interface RebuildPatch {
  race_index?: string;
  subrace_index?: string;
  class_index?: string;
  subclass_index?: string;
  background_index?: string;
}

// ── Games & seats ─────────────────────────────────────────────────────────
export interface GameBrief {
  id: string;
  name: string;
  created: number;
  players: number;
  scene: string;
  owner_id?: string | null;
  owner_name?: string | null;
  visibility?: "public" | "private";
  is_owner?: boolean;
  joined?: boolean;
  open_seats?: number;
  seats_total?: number;
  human_players?: number;
  started?: boolean;
  roster?: { label: string; kind: SeatKind; user_id: string | null }[];
}
export interface GamesListResponse {
  games: GameBrief[];
  user?: AuthUser | null;
}

export interface Seat {
  id: string;
  kind: SeatKind;
  label: string;
  persona: string | null;
  connected: boolean;
  character_id: string | null;
  user_id?: string | null;
  character: FullCharacter | null;
}

export interface Quest {
  id?: string;
  text: string;
  desc?: string;
  complete: boolean;
}

export interface PartyMember {
  seat: string;
  controller: string;
  label: string;
  id: string;
  name: string;
  summary: string;
  hp: number;
  max_hp: number;
  temp_hp: number;
  ac: number;
  level: number;
  // Present when the backend includes it in the party snapshot; used to light the
  // level-up affordance (xpQualifiesForLevel). Omitted today → affordance stays dark.
  xp?: number;
  conditions: string[];
  concentration: string | null;
}

export interface Combatant {
  id: string;
  name: string;
  side: "party" | "foe" | "neutral";
  // Party rows keep exact numbers; FOE rows in the player/spectator snapshot are
  // redacted — the backend omits hp/max_hp/ac and sends only a coarse wound band.
  // NOTE: hp/max_hp stay non-optional here on purpose — CombatTracker.tsx:117 reads
  // them unguarded (`!redacted && c.max_hp > 0 ? c.hp / c.max_hp : 0`), so widening
  // them to optional fails tsc in a file this task can't touch. Foe rows still omit
  // them at runtime; consumers already null-check (`c.hp == null`). `ac` is optional
  // because nothing reads it in an unguarded numeric context.
  hp: number;
  max_hp: number;
  temp_hp: number;
  ac?: number;
  initiative: number;
  conditions: string[];
  concentration: string | null;
  is_current: boolean;
  alive: boolean;
  conscious: boolean;
  is_pc?: boolean;
  character_id?: string | null;
  active_effects?: string[];
  used_action?: boolean;
  used_bonus?: boolean;
  used_reaction?: boolean;
  movement_used?: number;
  speed?: number;
  // Redacted-foe wound band (player/spectator view): coarse tier + bucketed 0..1 fill.
  wound_tier?: string;
  wound_frac?: number;
}
export interface Combat {
  name: string;
  round: number;
  current_turn: string;
  order: Combatant[];
}

export interface Snapshot {
  scene: string;
  party: PartyMember[];
  quests: Quest[];
  combat: Combat | null;
  party_gold: number;
  pending_rolls?: PendingRoll[];
  // Conductor spotlight (out of combat). awaiting = the character_id whose moment it is
  // right now, or null = open floor (any human may act). spotlight_cue = the in-fiction
  // line inviting that actor to act. Set by the DM's `conduct` tool; whose turn it is is
  // NOT secret, so every role sees these.
  awaiting?: string | null;
  spotlight_cue?: string;
}

export interface PendingRoll {
  id: string;
  character_id: string;
  character_name: string;
  kind: string;            // "check" | "save"
  skill?: string | null;
  ability: string;
  advantage: string;       // normal | advantage | disadvantage
  reason: string;          // player-facing prompt; DC is NEVER sent
}

export interface FullGame {
  id: string;
  name: string;
  scene: string;
  dm_style: DmStyle;
  setting?: string;
  tone_notes?: string;
  quests: Quest[];
  world_notes: string[];
  party_gold: number;
  seats: Seat[];
  snapshot: Snapshot;
  log_len: number;
}

export interface CreateGameResponse {
  id: string;
  game: {
    id: string;
    name: string;
    scene: string;
    dm_style: DmStyle;
    seats: { id: string; kind: SeatKind; label: string; character_id: string | null }[];
  };
}

export interface SeatAssignResponse {
  seat: string;
  character: FullCharacter;
}

// ── Log ─────────────────────────────────────────────────────────────────────
export interface LogEntry {
  id: string;
  ts: number;
  kind: LogKind;
  speaker: string | null;
  speaker_id: string | null;
  text: string;
  data: Record<string, unknown>;
}
export interface LogResponse {
  total: number;
  entries: LogEntry[];
}

// ── Compendium ──────────────────────────────────────────────────────────────
export type CompendiumKind =
  | "spells"
  | "monsters"
  | "magic-items"
  | "equipment"
  | "conditions"
  | "rules"
  | "rule-sections"
  | "classes"
  | "races"
  | "backgrounds"
  | "feats"
  | "skills";

export interface CompendiumResult {
  index: string;
  name: string;
  level?: number | null;
  school?: string | null;
  challenge_rating?: number | null;
  rarity?: string | null;
}
export interface CompendiumListResponse {
  kind: CompendiumKind;
  count: number;
  results: CompendiumResult[];
}

// Detail entries are raw SRD — typed loosely.
export type CompendiumDetail = Record<string, any>;

// ── WebSocket events ──────────────────────────────────────────────────────
export interface WsInit {
  type: "init";
  game: FullGame;
  you?: AuthUser | null;
}
// Out-of-band full-game refresh (seat claims/releases, roster changes).
export interface WsGame {
  type: "game";
  game: FullGame;
}
export interface WsPresence {
  type: "presence";
  present: { id: string; display_name: string }[];
}
export interface WsChat {
  type: "chat";
  who: string;
  user_id: string | null;
  text: string;
  ts: number;
}
export interface WsThinking {
  type: "thinking";
  who: string;
  role: "dm" | "player";
}
export interface WsArt {
  type: "art";
  kind: "character" | "item" | "spell" | "scene";
  name: string | null;
  character_id: string | null;
  url: string;
}
export interface WsTool {
  type: "tool";
  name: string;
  input: Record<string, any>;
  result: Record<string, any> | string | number | null;
}
export interface WsLog {
  type: "log";
  entry: LogEntry;
}
export interface WsState {
  type: "state";
  snapshot: Snapshot;
  log_len: number;
}
export interface WsError {
  type: "error";
  message: string;
}
// Streaming DM prose (C3): a provisional log item grows as chunks arrive, then the
// matching WsLog finalizes it. entry_id ties chunks to that log entry.
export interface WsNarrationDelta {
  type: "narration_delta";
  entry_id: string;
  text: string;
}
export type WsEvent = WsInit | WsGame | WsPresence | WsChat | WsThinking | WsArt | WsTool | WsLog | WsState | WsError | WsNarrationDelta;

// Client → server
export type WsClientMessage =
  | { action: "begin" }
  | { action: "continue" }
  | { action: "undo" }
  | { action: "chat"; text: string }
  | { action: "player_action"; character_id: string; text: string; kind: "action" | "speech" }
  | { action: "dm_direct"; text: string }
  | { action: "ai_player_turn"; character_id: string; prompt?: string }
  | { action: "advance_combat" }
  | {
      action: "player_attack";
      character_id: string;
      weapon_index: string;
      target_id: string;
      advantage?: string;
      two_handed?: boolean;
      sneak_attack?: boolean;
      smite_level?: number;
      offhand?: boolean;
    }
  | {
      action: "player_cast";
      character_id: string;
      spell_index: string;
      target_ids: string[];
      slot_level?: number;
    }
  | { action: "player_use_feature"; character_id: string; feature_key: string; mode?: string }
  | { action: "player_end_turn"; character_id: string }
  | { action: "player_roll"; roll_id: string }
  | { action: "ping" };

// ── Stream items (UI-side fusion of logs + inline tool beats) ──────────────
export type StreamItem =
  | { kind: "log"; uid: string; entry: LogEntry }
  | {
      kind: "tool";
      uid: string;
      name: string;
      input: Record<string, any>;
      result: Record<string, any> | string | number | null;
      ts: number;
    };
